import React, { useState, useRef } from "react";
import { cn } from "../../lib/helpers";
import * as styles from "../global/global.module.css";
import objectionsData from "../../../static/Objections.json";

const TOPICS = [
  {
    id: "general",
    label: "General Interest",
    description: "Value proposition, why choose us",
  },
  {
    id: "competitive",
    label: "Competitive Comparison",
    description: "How we compare to competitors",
  },
  {
    id: "product",
    label: "Product & Technical",
    description: "How it works, capabilities",
  },
  {
    id: "integration",
    label: "Integration & Implementation",
    description: "Tech stack fit, setup process",
  },
  {
    id: "strategy",
    label: "Sales & Strategy",
    description: "Team adoption, strategic value",
  },
];

const DETAILS = [
  // General
  { topicId: "general", id: "value-prop", label: "Core Value Proposition" },

  // Competitive
  { topicId: "competitive", id: "competitors", label: "Specific Competitors" },
  { topicId: "competitive", id: "differentiation", label: "Key Differentiators" },
  { topicId: "competitive", id: "replacement", label: "Tool Replacement" },

  // Product & Technical
  { topicId: "product", id: "data-concerns", label: "Data & Accuracy" },
  { topicId: "product", id: "technical-capabilities", label: "Technical Capabilities" },
  { topicId: "product", id: "compliance", label: "Compliance & Privacy" },

  // Integration
  { topicId: "integration", id: "tech-stack", label: "Technology Stack" },
  { topicId: "integration", id: "implementation", label: "Implementation Process" },

  // Strategy
  { topicId: "strategy", id: "team-adoption", label: "Team Adoption" },
  { topicId: "strategy", id: "strategic-value", label: "Strategic Value" },
];

export const ObjectionsSelector = ({ title, description, bgColor, className }) => {
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [activeObjection, setActiveObjection] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedDetails, setSelectedDetails] = useState([]);
  const contentCanvasRef = useRef(null);

  // Function to get objections based on topic/detail selection
  const getObjectionsForSelection = (topics, details) => {
    if (topics.length === 0) return objectionsData;

    let filtered = [];

    topics.forEach(topicId => {
      switch (topicId) {
        case "general":
          filtered.push(...objectionsData.filter(obj =>
            obj.category === "Sales & Strategy" && obj.tagline === "Value Proposition"
          ));
          break;
        case "competitive":
          filtered.push(...objectionsData.filter(obj => obj.category === "Competitive"));
          break;
        case "product":
          filtered.push(...objectionsData.filter(obj => obj.category === "Product & Features"));
          break;
        case "integration":
          filtered.push(...objectionsData.filter(obj => obj.category === "Integration & Process"));
          break;
        case "strategy":
          filtered.push(...objectionsData.filter(obj => obj.category === "Sales & Strategy"));
          break;
        default:
          break;
      }
    });

    // Further filter by details if selected
    if (details.length > 0) {
      filtered = filtered.filter(obj => {
        return details.some(detailId => {
          switch (detailId) {
            case "value-prop":
              return obj.tagline === "Value Proposition";
            case "competitors":
              return obj.tagline.startsWith("vs ");
            case "differentiation":
              return obj.tagline === "Identity Matching" || obj.tagline === "vs 6Sense";
            case "replacement":
              return obj.tagline === "Tool Replacement";
            case "data-concerns":
              return obj.tagline.includes("Data") || obj.tagline === "Contact Verification";
            case "technical-capabilities":
              return obj.tagline === "Signal De-anonymization" || obj.tagline === "AI Prompting";
            case "compliance":
              return obj.tagline === "Privacy Compliance";
            case "tech-stack":
              return obj.tagline === "Tech Stack";
            case "implementation":
              return obj.tagline === "CRM Integration" || obj.tagline === "DSP Capabilities";
            case "team-adoption":
              return obj.tagline === "Tool Fatigue" || obj.tagline === "Team Alignment";
            case "strategic-value":
              return obj.tagline === "Value Proposition";
            default:
              return true;
          }
        });
      });
    }

    // Remove duplicates
    return filtered.filter((obj, index, self) =>
      index === self.findIndex(o => o.id === obj.id)
    );
  };

  // Get objections based on current topic/detail selection
  const topicFilteredObjections = getObjectionsForSelection(selectedTopics, selectedDetails);

  // Filter objections based on search term and topic/detail selection
  const filteredObjections = topicFilteredObjections.filter(objection =>
    objection.Questions.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (objection.tagline && objection.tagline.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (objection.Answers && objection.Answers.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (objection["Talk-Track"] && objection["Talk-Track"].toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Function to handle card hover
  const handleCardHover = (objection) => {
    if (!activeObjection) {
      setSelectedObjection(objection);
    }
  };

  // Function to handle card leave
  const handleCardLeave = () => {
    if (!activeObjection) {
      setSelectedObjection(null);
    }
  };

  // Function to handle card click
  const handleCardClick = (objection) => {
    setActiveObjection(objection);
    setSelectedObjection(objection);

    if (contentCanvasRef.current) {
      const element = contentCanvasRef.current;
      const offsetTop = element.offsetTop - 80;

      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  // Function to clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSelectedObjection(null);
    setActiveObjection(null);
  };

  // Function to handle topic selection
  const handleTopicChange = (topicId) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
    setSelectedDetails([]);
    setSelectedObjection(null);
    setActiveObjection(null);
  };

  // Function to handle detail selection
  const handleDetailChange = (detailId) => {
    setSelectedDetails(prev =>
      prev.includes(detailId)
        ? prev.filter(id => id !== detailId)
        : [...prev, detailId]
    );
    setSelectedObjection(null);
    setActiveObjection(null);
  };

  // Function to handle objection click from decision tree
  const handleObjectionClick = (objectionId) => {
    const objection = objectionsData.find(obj => obj.id === objectionId);
    if (objection) {
      setActiveObjection(objection);
      setSelectedObjection(objection);

      if (contentCanvasRef.current) {
        const element = contentCanvasRef.current;
        const offsetTop = element.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedTopics([]);
    setSelectedDetails([]);
    setSearchTerm("");
    setSelectedObjection(null);
    setActiveObjection(null);
  };

  const availableDetails = DETAILS.filter(detail =>
    selectedTopics.length === 0 || selectedTopics.includes(detail.topicId)
  );

  // Function to get category border color
  const getCategoryBorderColor = (category) => {
    const colorMap = {
      "Product & Features": "border-melon",
      "Competitive": "border-lemon",
      "Integration & Process": "border-robin",
      "Sales & Strategy": "border-peach"
    };
    return colorMap[category] || "border-gray-200";
  };

  // Function to get category background color for pills
  const getCategoryBgColor = (category) => {
    const colorMap = {
      "Product & Features": "bg-melon",
      "Competitive": "bg-lemon",
      "Integration & Process": "bg-robin",
      "Sales & Strategy": "bg-peach"
    };
    return colorMap[category] || "bg-gray-200";
  };

  return (
    <section className={cn(styles.section, styles[bgColor], className)}>
      <div className={styles.container}>
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {description && (
              <p className={styles.description}>{description}</p>
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto space-y-8">
          {/* Decision Tree Navigation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: Topics */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">1. Topic</h4>
                <div className="space-y-2">
                  {TOPICS.map(topic => (
                    <label key={topic.id} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTopics.includes(topic.id)}
                        onChange={() => handleTopicChange(topic.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{topic.label}</div>
                        <div className="text-xs text-gray-500">{topic.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Column 2: Details */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">2. Detail (Optional)</h4>
                {selectedTopics.length === 0 ? (
                  <p className="text-sm text-gray-500">Select topic first</p>
                ) : (
                  <div className="space-y-2">
                    {availableDetails.map(detail => (
                      <label key={detail.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDetails.includes(detail.id)}
                          onChange={() => handleDetailChange(detail.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-900">{detail.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Column 3: Filtered Objections */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">
                  3. Objections
                  {topicFilteredObjections.length > 0 && (
                    <span className="text-sm font-normal text-gray-500">({topicFilteredObjections.length})</span>
                  )}
                </h4>
                <div className="border-2 border-blue-300 bg-white rounded-lg p-2 shadow-md" style={{ minHeight: '180px', maxHeight: '260px', overflowY: 'scroll' }}>
                  {topicFilteredObjections.length === 0 ? (
                    <p className="text-sm text-gray-500 p-2">
                      {selectedTopics.length === 0 ? "All objections shown below" : "No matching objections"}
                    </p>
                  ) : (
                    <div>
                      {topicFilteredObjections.map(objection => (
                        <button
                          key={objection.id}
                          onClick={() => handleObjectionClick(objection.id)}
                          className="block w-full text-left px-2 py-0.5 text-sm text-blue-700 hover:text-blue-900 hover:bg-robin rounded transition-colors"
                          style={{ marginBottom: '1px', lineHeight: '1.2', fontSize: '0.97rem' }}
                        >
                          {objection.tagline}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Summary */}
            {(selectedTopics.length > 0 || selectedDetails.length > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-gray-600">Filtering by:</span>
                  {selectedTopics.map(topicId => {
                    const topic = TOPICS.find(t => t.id === topicId);
                    return (
                      <span key={topicId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-robin text-robinDark">
                        {topic?.label}
                      </span>
                    );
                  })}
                  {selectedDetails.map(detailId => {
                    const detail = DETAILS.find(d => d.id === detailId);
                    return (
                      <span key={detailId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-mint text-mintDark">
                        {detail?.label}
                      </span>
                    );
                  })}
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Questions Grid Section */}
          <div className="space-y-4">
            {/* Search Box */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search by tagline, question, or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              {(selectedTopics.length > 0 || selectedDetails.length > 0 || searchTerm) && (
                <div className="text-sm text-gray-600">
                  Showing {filteredObjections.length} of {objectionsData.length} objections
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {filteredObjections.map((objection, index) => (
                <div
                  key={objection.Questions}
                  id={`objection-${objection.id}`}
                  className={cn(
                    "p-4 group border-2 rounded-lg cursor-pointer transition-all duration-200",
                    "hover:border-green-emerald hover:shadow-md",
                    activeObjection === objection
                      ? "border-black shadow-md bg-light-60"
                      : selectedObjection === objection
                        ? "border-black shadow-md bg-light-60"
                        : "border-transparent bg-white"
                  )}
                  onMouseEnter={() => handleCardHover(objection)}
                  onMouseLeave={handleCardLeave}
                  onClick={() => handleCardClick(objection)}
                >
                  <div className="space-y-3">
                    <p className="text-xl font-medium text-gray-900 leading-5">
                      {objection.tagline}
                    </p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs text-black ${getCategoryBgColor(objection.category)} mt-1`}>
                      {objection.category}
                    </span>
                    <p className="text-sm text-secondary leading-snug">
                      {objection.Questions}
                    </p>
                  </div>
                </div>
              ))}

              {filteredObjections.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <p>
                    {searchTerm
                      ? `No objections found matching "${searchTerm}"`
                      : "No objections match your selected filters"
                    }
                  </p>
                  <button
                    onClick={searchTerm ? clearSearch : clearAllFilters}
                    className="mt-2 text-blue-500 hover:text-blue-700 text-sm"
                  >
                    {searchTerm ? "Clear search" : "Clear filters"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content Canvas */}
          <div ref={contentCanvasRef} className="w-full">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm min-h-[400px]">
              {selectedObjection ? (
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="text-2xl">❓</div>
                    <h4 className="text-xl font-semibold text-gray-900 leading-tight">
                      {selectedObjection.Questions}
                    </h4>
                  </div>

                  {selectedObjection.Answers && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="text-lg">💡</div>
                        <h5 className="text-lg font-medium text-gray-800">
                          Detailed Answer
                        </h5>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                          {selectedObjection.Answers}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedObjection["Talk-Track"] &&
                    selectedObjection["Talk-Track"] !== "<-----" && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="text-lg">🗣️</div>
                          <h5 className="text-lg font-medium text-gray-800">
                            Talk Track
                          </h5>
                        </div>
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                          <p className="text-blue-900 whitespace-pre-line leading-relaxed">
                            {selectedObjection["Talk-Track"]}
                          </p>
                        </div>
                      </div>
                    )}

                  {selectedObjection.Name && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm">👤</div>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Owner:</span> {selectedObjection.Name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full p-12">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-4">💬</div>
                    <p className="text-lg font-medium mb-2">Select an objection</p>
                    <p className="text-sm">
                      Hover over or click on a question to see the response details
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
